import { Route, Switch } from "wouter";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { titled } from "./titled";
import { ProtectedRoute } from "@/shared/routes/ProtectedRoute";
import { ADMIN_SIGN_IN_PATH } from "@/shared/routes/adminAccess";
import { NotFoundPage } from "@/features/app/pages/NotFoundPage";
import LandingPage from "@/features/landing/pages/LandingPage";
import ForAgenciesPage from "@/features/landing/pages/ForAgenciesPage";
import ForClippingPlatformsPage from "@/features/landing/pages/ForClippingPlatformsPage";
import { articleRegistry, BLOG_PATH } from "@/features/blog/articleRegistry";
import {
  EXTERNAL_INVITATION_LINK_PATH,
  PARTICIPATION_INVITATION_PATH,
} from "@/features/participationInvitation/constants";

const SignInPage = lazyWithRetry(() => import("@/features/auth/pages/sign-in/SignInPage"));
const AuthYouTubePage = lazyWithRetry(() => import("@/features/auth/pages/youtube/AuthYouTubePage"));
const AuthYouTubeCallbackPage = lazyWithRetry(() => import("@/features/auth/pages/youtube/AuthYouTubeCallbackPage"));
const AuthMicrosoftCallbackPage = lazyWithRetry(
  () => import("@/features/auth/pages/microsoft/AuthMicrosoftCallbackPage"),
);
const AuthLoginWithDiscordPage = lazyWithRetry(() => import("@/features/auth/pages/discord/AuthLoginWithDiscordPage"));
const AuthLoginWithDiscordCallbackPage = lazyWithRetry(
  () => import("@/features/auth/pages/discord/AuthLoginWithDiscordCallbackPage"),
);
const AuthPaypalCallbackPage = lazyWithRetry(() => import("@/features/auth/pages/paypal/AuthPaypalCallbackPage"));
const AuthGoogleMcpLoginPage = lazyWithRetry(() => import("@/features/auth/pages/mcp/AuthGoogleMcpLoginPage"));
const AuthMcpCompleteAuthorizePage = lazyWithRetry(
  () => import("@/features/auth/pages/mcp/AuthMcpCompleteAuthorizePage"),
);
const AcceptInvitationPage = lazyWithRetry(() => import("@/features/invitation/pages/AcceptInvitationPage"));
const FillDocumentPage = lazyWithRetry(() => import("@/features/document/pages/FillDocumentPage"));

const CreatorManagePaymentMethodsPage = lazyWithRetry(
  () => import("@/features/payment/CreatorManagePaymentMethodsPage"),
);
const CreatorCampaignPage = lazyWithRetry(() => import("@/features/campaign/pages/CreatorCampaignPage"));
const CreatorSubmitRedirect = lazyWithRetry(() => import("@/features/campaign/pages/CreatorSubmitRedirect"));
const PrescreenPage = lazyWithRetry(() => import("@/features/prescreen/pages/PrescreenPage"));
const ParticipationInvitationPage = lazyWithRetry(
  () => import("@/features/participationInvitation/pages/ParticipationInvitationPage"),
);
const ExternalInvitationLinkPage = lazyWithRetry(
  () => import("@/features/participationInvitation/pages/ExternalInvitationLinkPage"),
);
const ProofCallbackPage = lazyWithRetry(() => import("@/features/proof/pages/ProofCallbackPage"));
const CreatorStartProofPage = lazyWithRetry(() => import("@/features/proof/pages/CreatorStartProofPage"));
const ProofSessionPage = lazyWithRetry(() => import("@/features/proofSession/pages/ProofSessionPage"));
const CampaignMediaPage = lazyWithRetry(() => import("@/features/campaign/pages/CampaignMediaPage"));
const CampaignSummaryPage = lazyWithRetry(() => import("@/features/campaign/pages/CampaignSummaryPage"));
const TermsOfServicePage = lazyWithRetry(() => import("@/features/legal/pages/TermsOfServicePage"));
const PrivacyPolicyPage = lazyWithRetry(() => import("@/features/legal/pages/PrivacyPolicyPage"));
const BlogPage = lazyWithRetry(() => import("@/features/blog/pages/BlogPage"));
const ArticlePage = lazyWithRetry(() => import("@/features/blog/pages/ArticlePage"));
const ProfilePage = lazyWithRetry(() => import("@/features/profile/pages/ProfilePage"));
const ProfileAccountAddedPage = lazyWithRetry(() => import("@/features/profile/pages/ProfileAccountAddedPage"));
const AdminRoutes = lazyWithRetry(() => import("./AdminRoutes"));
// Not one of AdminRoutes, so signing in doesn't download the whole admin app: see AdminSignInRoute.
const AdminSignInRoute = lazyWithRetry(() => import("./AdminSignInRoute"));

export function AppRoutes() {
  return (
    <Switch>
      <Route path="/auth/google/callback">{titled("Signing in with Google", <AuthYouTubeCallbackPage />)}</Route>
      <Route path="/auth/google">{titled("Connect YouTube", <AuthYouTubePage />)}</Route>
      <Route path="/auth/microsoft/callback">{titled("Signing in", <AuthMicrosoftCallbackPage />)}</Route>
      <Route path="/auth/discord/callback">{titled("Connecting Discord", <AuthLoginWithDiscordCallbackPage />)}</Route>
      <Route path="/auth/discord">{titled("Connect Discord", <AuthLoginWithDiscordPage />)}</Route>
      <Route path="/auth/paypal/callback">{titled("Connecting PayPal", <AuthPaypalCallbackPage />)}</Route>
      <Route path="/auth/mcp/authorize">{titled("Authorize access", <AuthGoogleMcpLoginPage />)}</Route>
      <Route path="/auth/mcp/complete">{titled("Authorize access", <AuthMcpCompleteAuthorizePage />)}</Route>
      <Route path="/for-agencies">
        <ForAgenciesPage />
      </Route>
      <Route path="/for-clipping-platforms">
        <ForClippingPlatformsPage />
      </Route>
      <Route path="/signin">
        <SignInPage />
      </Route>
      <Route path="/terms-of-service">
        <TermsOfServicePage />
      </Route>
      <Route path="/privacy-policy">
        <PrivacyPolicyPage />
      </Route>
      <Route path={BLOG_PATH}>
        <BlogPage />
      </Route>
      {articleRegistry.map((article) => (
        <Route key={article.slug} path={article.path}>
          <ArticlePage article={article} />
        </Route>
      ))}
      <Route path="/invitation/accept/:token">
        {({ token }) => titled("Accept invitation", <AcceptInvitationPage token={token} />)}
      </Route>
      <Route path="/account/document/:templateId">
        {({ templateId }) =>
          titled(
            "Complete document",
            <ProtectedRoute>
              {/* Keyed so navigating between templates remounts with fresh local state. */}
              <FillDocumentPage key={templateId} templateId={templateId} />
            </ProtectedRoute>,
          )
        }
      </Route>
      {/*
        All three proof-callback routes render the same component inside this
        Switch, so each keys it by request — mirrors the start-proof routes
        below (see the comment there) so an in-app navigation between two
        proof-callback URLs always remounts fresh state, in particular
        useProofCallbackMeta's one-time read of the URL's status/code params.
      */}
      <Route path="/proof/video/callback/:proofRequestId">
        {({ proofRequestId }) =>
          titled(
            "Video verification",
            <ProofCallbackPage key={`video:${proofRequestId}`} type="video" proofRequestId={proofRequestId} />,
          )
        }
      </Route>
      <Route path="/proof/video-stats/callback/:proofRequestId">
        {({ proofRequestId }) =>
          titled(
            "Video stats verification",
            <ProofCallbackPage
              key={`videoStats:${proofRequestId}`}
              type="videoStats"
              proofRequestId={proofRequestId}
            />,
          )
        }
      </Route>
      <Route path="/proof/channel-stats/callback/:proofRequestId">
        {({ proofRequestId }) =>
          titled(
            "Channel stats verification",
            <ProofCallbackPage
              key={`channelStats:${proofRequestId}`}
              type="channelStats"
              proofRequestId={proofRequestId}
            />,
          )
        }
      </Route>
      {/* Keyed by token like the callbacks: the page reads Vouch's status/code params once. */}
      <Route path="/proof/session/:token">
        {({ token }) => titled("Verification", <ProofSessionPage key={token} token={token} />)}
      </Route>
      {/*
        All proof-start routes render the same component inside this Switch,
        so each keys it by the proof it is starting. Without that, an in-app
        navigation between two proof URLs would reuse the mounted instance and
        CreatorStartProofPage's once-per-mount start would suppress the second
        proof. Same reason FillDocumentPage is keyed above.
      */}
      <Route path="/creator/post/:platformId/:postId/start-video-proof">
        {({ platformId, postId }) =>
          titled(
            "Verify video",
            <CreatorStartProofPage
              key={`video:${platformId}:${postId}`}
              type="video"
              platformId={platformId}
              entityId={postId}
            />,
          )
        }
      </Route>
      <Route path="/creator/post/:platformId/:postId/start-video-stats-proof">
        {({ platformId, postId }) =>
          titled(
            "Verify video stats",
            <CreatorStartProofPage
              key={`videoStats:${platformId}:${postId}`}
              type="videoStats"
              platformId={platformId}
              entityId={postId}
            />,
          )
        }
      </Route>
      <Route path="/creator/proof/channel/:platformId/:accountId/start-channel-stats-proof">
        {({ platformId, accountId }) =>
          titled(
            "Verify channel stats",
            <CreatorStartProofPage
              key={`channelStats:${platformId}:${accountId}`}
              type="channelStats"
              platformId={platformId}
              entityId={accountId}
            />,
          )
        }
      </Route>
      <Route path="/profile/added">{titled("Account added", <ProfileAccountAddedPage />)}</Route>
      <Route path="/profile">
        {titled(
          "Profile",
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>,
        )}
      </Route>

      <Route path="/creator/payment/stripe-refresh">
        {titled(
          "Payment methods",
          <ProtectedRoute unauthenticatedFallback="reinvite">
            <CreatorManagePaymentMethodsPage />
          </ProtectedRoute>,
        )}
      </Route>
      <Route path="/creator/payment">
        {titled(
          "Payment methods",
          <ProtectedRoute unauthenticatedFallback="reinvite">
            <CreatorManagePaymentMethodsPage />
          </ProtectedRoute>,
        )}
      </Route>
      <Route path="/creator/prescreen/:attemptId">
        {({ attemptId }) =>
          titled(
            "Prescreen",
            <ProtectedRoute unauthenticatedFallback="reinvite">
              <PrescreenPage attemptId={attemptId} />
            </ProtectedRoute>,
          )
        }
      </Route>
      <Route path={EXTERNAL_INVITATION_LINK_PATH}>
        {({ token }) => titled("Campaign invitation", <ExternalInvitationLinkPage key={token} token={token} />)}
      </Route>
      <Route path={PARTICIPATION_INVITATION_PATH}>
        {({ creatorNetworkId, campaignId }) =>
          titled(
            "Campaign invitation",
            <ProtectedRoute unauthenticatedFallback="reinvite">
              <ParticipationInvitationPage creatorNetworkId={creatorNetworkId} campaignId={campaignId} />
            </ProtectedRoute>,
          )
        }
      </Route>
      <Route path="/creator/submit">{titled("Submit clips", <CreatorSubmitRedirect />)}</Route>
      <Route path="/creator/campaign/:token?">
        {({ token }) => titled("Campaign", <CreatorCampaignPage key={token} token={token} />)}
      </Route>
      <Route path="/campaign/:campaignId/media">
        {({ campaignId }) => titled("Campaign media", <CampaignMediaPage campaignId={campaignId} />)}
      </Route>
      <Route path="/campaign/:campaignId/summary">
        {({ campaignId }) => titled("Campaign summary", <CampaignSummaryPage campaignId={campaignId} />)}
      </Route>
      <Route path={ADMIN_SIGN_IN_PATH}>
        <AdminSignInRoute />
      </Route>
      <Route path="/admin/*?">
        <AdminRoutes />
      </Route>
      <Route path="/">
        <LandingPage />
      </Route>
      <Route path="/404">
        <NotFoundPage />
      </Route>
      <Route>
        <NotFoundPage />
      </Route>
    </Switch>
  );
}
